import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Contact } from '../../types/crm';
import type { TicketAttachment, TicketComposeMode, TicketMessage } from '../../types/tickets';
import {
  preserveEmailBlankLines,
  stripEmptyComposeHtml,
} from '../../lib/ticketEmailSignature';
import { listReplySignatures, resolvePortalMailboxId, type ReplySignature } from '../../lib/tickets';
import {
  buildQuotedThreadHtml,
  customerVisibleThreadMessages,
  resolveReplyRecipients,
  wrapComposeHtml,
} from '../../lib/ticketComposeHelpers';
import { MemoBodyEditor } from '../forms/MemoBodyEditor';
import { Modal } from '../ui/Modal';
import {
  EmailRecipientChips,
  parseRecipientString,
  recipientsToEmails,
  type EmailRecipient,
} from './EmailRecipientChips';
import { TicketMessageTimeline } from './TicketMessageTimeline';
import { IsolatedEmailHtml } from './IsolatedEmailHtml';

type PendingFile = {
  path: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
};

type Props = {
  open: boolean;
  mode: TicketComposeMode;
  subject: string;
  defaultTo: string;
  defaultCc?: string;
  messages: TicketMessage[];
  attachmentsByMessage?: Record<string, TicketAttachment[]>;
  mailboxEmail?: string;
  /** Mailbox owning reply signatures; falls back to portal default. */
  mailboxId?: string | null;
  agentEmail?: string;
  accountContacts?: Contact[];
  busy?: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<PendingFile>;
  onSend: (payload: {
    bodyHtml: string;
    previewHtml?: string;
    toEmails: string[];
    ccEmails: string[];
    attachments: PendingFile[];
    mode: TicketComposeMode;
    subject: string;
  }) => Promise<void>;
};

const MODE_LABEL: Record<TicketComposeMode, string> = {
  reply: 'Reply',
  reply_all: 'Reply all',
  forward: 'Forward',
  new: 'New email',
};

function contactLabel(c: Contact): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} <${c.email}>` : c.email || c.id;
}

/** Shared General signature only — never auto-pick a personal reply signature. */
function pickDefaultSignatureSlug(list: ReplySignature[]): string {
  const general =
    list.find((s) => /^general$/i.test(s.name.trim())) ||
    list.find((s) => s.slug === 'reply_signature');
  return general?.slug ?? '';
}

const HtmlIsland = memo(function HtmlIsland({
  className,
  html,
}: {
  className: string;
  html: string;
}) {
  return <IsolatedEmailHtml className={className} html={html} />;
});

const TicketMessageEditor = memo(function TicketMessageEditor({
  disabled,
  onHtmlChange,
}: {
  disabled: boolean;
  onHtmlChange: (html: string) => void;
}) {
  const [html, setHtml] = useState('');
  return (
    <MemoBodyEditor
      value={html}
      onChange={(next) => {
        setHtml(next);
        onHtmlChange(next);
      }}
      disabled={disabled}
      allowImages
      emailFormat
      defaultEmailFont
      placeholder="Enter your message"
    />
  );
});

export function TicketComposeModal({
  open,
  mode,
  subject,
  defaultTo,
  defaultCc = '',
  messages,
  attachmentsByMessage = {},
  mailboxEmail = '',
  mailboxId = null,
  agentEmail = '',
  accountContacts = [],
  busy,
  onClose,
  onUpload,
  onSend,
}: Props) {
  const bodyRef = useRef('');
  const recipientsSeedRef = useRef('');
  const [editorEpoch, setEditorEpoch] = useState(0);
  const onBodyHtmlChange = useCallback((html: string) => {
    bodyRef.current = html;
  }, []);

  const [signatures, setSignatures] = useState<ReplySignature[]>([]);
  const [selectedSigSlug, setSelectedSigSlug] = useState<string>('');
  const [signatureExpanded, setSignatureExpanded] = useState(false);
  const [toRecipients, setToRecipients] = useState<EmailRecipient[]>([]);
  const [ccRecipients, setCcRecipients] = useState<EmailRecipient[]>([]);
  const [composeSubject, setComposeSubject] = useState(subject);
  const [showCc, setShowCc] = useState(Boolean(defaultCc));
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const contactOptions = useMemo(
    () =>
      accountContacts
        .filter((c) => c.email?.includes('@'))
        .map((c) => ({ email: String(c.email), label: contactLabel(c) })),
    [accountContacts],
  );

  const selectedSignature = useMemo(
    () => signatures.find((s) => s.slug === selectedSigSlug) ?? null,
    [signatures, selectedSigSlug],
  );

  const visibleMessages = useMemo(
    () => customerVisibleThreadMessages(messages),
    [messages],
  );

  const showThread =
    (mode === 'reply' || mode === 'reply_all' || mode === 'forward') && visibleMessages.length > 0;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setFiles([]);
    bodyRef.current = '';
    setEditorEpoch((n) => n + 1);
    setSignatureExpanded(false);

    void (async () => {
      try {
        const id = mailboxId || (await resolvePortalMailboxId());
        if (!id) {
          setSignatures([]);
          setSelectedSigSlug('');
          return;
        }
        const list = await listReplySignatures(id);
        const active = list.filter((s) => s.isActive && s.html.trim());
        setSignatures(active);
        // Always default to General; agents pick personal signatures themselves.
        setSelectedSigSlug(mode === 'forward' ? '' : pickDefaultSignatureSlug(active));
      } catch {
        setSignatures([]);
        setSelectedSigSlug('');
      }
    })();

    if (mode === 'forward') {
      setComposeSubject(/^fwd:/i.test(subject) ? subject : `Fwd: ${subject}`);
    } else if (mode === 'new') {
      setComposeSubject('');
    } else {
      setComposeSubject(subject);
    }
    // Reset compose chrome only when opening or switching mode — not on message refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, mailboxId]);

  useEffect(() => {
    if (!open) {
      recipientsSeedRef.current = '';
      return;
    }

    const seedKey = `${mode}::${messages.map((m) => m.id).join('|')}`;
    const modeSwitched = !recipientsSeedRef.current.startsWith(`${mode}::`);

    if (mode === 'forward' || mode === 'new') {
      if (modeSwitched) {
        setToRecipients(mode === 'forward' ? [] : parseRecipientString(defaultTo, contactOptions));
        setCcRecipients(parseRecipientString(defaultCc, contactOptions));
        setShowCc(Boolean(defaultCc.trim()));
        recipientsSeedRef.current = seedKey;
      }
      return;
    }

    const { to: toList, cc: ccList } = resolveReplyRecipients(messages, {
      mailboxEmail,
      agentEmail: agentEmail || '',
      fallbackTo: defaultTo,
      replyAll: mode === 'reply_all',
    });

    const nextTo = parseRecipientString(toList.join(', '), contactOptions);
    const nextCc = parseRecipientString(ccList.join(', '), contactOptions);

    if (modeSwitched || !recipientsSeedRef.current) {
      setToRecipients(nextTo);
      setCcRecipients(nextCc);
      setShowCc(nextCc.length > 0 || mode === 'reply_all');
      recipientsSeedRef.current = seedKey;
      return;
    }

    if (nextTo.length > 0) {
      setToRecipients((prev) => (prev.length > 0 ? prev : nextTo));
    }
    if (nextCc.length > 0) {
      setCcRecipients((prev) => (prev.length > 0 ? prev : nextCc));
      setShowCc(true);
    }
    recipientsSeedRef.current = seedKey;
  }, [open, mode, messages, mailboxEmail, agentEmail, defaultTo, defaultCc, contactOptions]);

  async function handleFiles(list: FileList | null) {
    if (!list?.length) return;
    setUploading(true);
    setError(null);
    try {
      const uploaded: PendingFile[] = [];
      for (const file of Array.from(list)) {
        uploaded.push(await onUpload(file));
      }
      setFiles((prev) => [...prev, ...uploaded]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }

  async function handleSend() {
    const messageHtml = stripEmptyComposeHtml(bodyRef.current);
    const sigHtml =
      selectedSigSlug && selectedSignature
        ? preserveEmailBlankLines(selectedSignature.html.trim())
        : '';
    const hasContent = Boolean(messageHtml || sigHtml || (mode === 'forward' && messages.length > 0));
    if (!hasContent) {
      setError('Message body is required.');
      return;
    }

    const toEmails = recipientsToEmails(toRecipients);
    if (toEmails.length === 0) {
      setError('At least one recipient is required.');
      return;
    }

    setError(null);
    let userHtml = messageHtml;
    if (sigHtml) {
      userHtml = userHtml ? `${userHtml}<br><br>${sigHtml}` : sigHtml;
    }

    const quotedForSend =
      (mode === 'forward' || mode === 'reply' || mode === 'reply_all') &&
      visibleMessages.length > 0
        ? buildQuotedThreadHtml(visibleMessages)
        : '';
    const fullHtml =
      mode === 'forward' || mode === 'reply' || mode === 'reply_all'
        ? wrapComposeHtml(userHtml, quotedForSend)
        : userHtml;

    onClose();

    // Server rebuilds quoted history from DB (internal notes excluded). Send user text only.
    void onSend({
      bodyHtml: userHtml,
      previewHtml: fullHtml,
      toEmails,
      ccEmails: recipientsToEmails(ccRecipients),
      attachments: files,
      mode,
      subject: composeSubject,
    }).catch(() => {
      /* parent surfaces errors */
    });
  }

  const ccToggle =
    !showCc ? (
      <button type="button" className="ticket-compose-modal__cc-toggle" onClick={() => setShowCc(true)}>
        Cc
      </button>
    ) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={MODE_LABEL[mode]}
      extraWide
      panelClassName={`modal-panel--ticket-compose${showThread ? ' modal-panel--ticket-compose-split' : ''}`}
    >
      <div className={`ticket-compose-modal${showThread ? ' ticket-compose-modal--split' : ''}`}>
        {showThread ? (
          <aside className="ticket-compose-modal__thread" aria-label="Previous messages">
            <h4 className="ticket-compose-modal__thread-title">
              Previous messages ({visibleMessages.length})
            </h4>
            <p className="ticket-compose-modal__thread-hint">
              Internal notes are not shown here and are never emailed to the customer.
            </p>
            <div className="ticket-compose-modal__thread-list">
              <TicketMessageTimeline
                messages={visibleMessages}
                attachmentsByMessage={attachmentsByMessage}
                accordion
              />
            </div>
          </aside>
        ) : null}

        <div className="ticket-compose-modal__compose">
          {mode === 'reply' || mode === 'reply_all' ? (
            <p className="ticket-compose-modal__send-warning">
              This reply will be emailed to the customer. For team-only notes, use{' '}
              <strong>Internal notes</strong> (Activity) instead.
            </p>
          ) : null}
          {(mode === 'forward' || mode === 'new') && (
            <label className="ticket-compose-modal__field">
              <span>Subject</span>
              <input
                className="field-input"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
              />
            </label>
          )}

          <EmailRecipientChips
            label="To"
            value={toRecipients}
            onChange={setToRecipients}
            contacts={contactOptions}
            disabled={Boolean(busy)}
            trailing={ccToggle}
          />

          {showCc ? (
            <EmailRecipientChips
              label="Cc"
              value={ccRecipients}
              onChange={setCcRecipients}
              contacts={contactOptions}
              disabled={Boolean(busy)}
            />
          ) : null}

          <div className="ticket-compose-modal__editor">
            <span className="ticket-compose-modal__field-label">Message</span>
            <TicketMessageEditor
              key={editorEpoch}
              disabled={Boolean(busy || uploading)}
              onHtmlChange={onBodyHtmlChange}
            />
          </div>

          <div className="ticket-compose-modal__signature">
            <div className="ticket-compose-modal__signature-bar">
              <label className="ticket-compose-modal__field ticket-compose-modal__field--sig">
                <span>Signature</span>
                <select
                  className="field-input field-input--compact"
                  value={selectedSigSlug}
                  disabled={busy}
                  onChange={(e) => setSelectedSigSlug(e.target.value)}
                >
                  <option value="">None</option>
                  {signatures.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              {selectedSignature ? (
                <button
                  type="button"
                  className="ticket-compose-modal__signature-collapse"
                  onClick={() => setSignatureExpanded((v) => !v)}
                >
                  {signatureExpanded ? 'Hide' : 'Show'}
                </button>
              ) : null}
            </div>
            {selectedSignature && signatureExpanded ? (
              <HtmlIsland
                className="ticket-compose-modal__signature-preview"
                html={selectedSignature.html}
              />
            ) : selectedSignature && !signatureExpanded ? (
              <p className="ticket-compose-modal__signature-collapsed">
                Signature will be added when you send. Manage signatures in Settings.
              </p>
            ) : (
              <p className="ticket-compose-modal__signature-collapsed">
                No signature. Manage signatures in Settings → Ticketing.
              </p>
            )}
          </div>

          <div className="ticket-compose-modal__actions">
            <label className="btn-secondary btn-sm ticket-compose-modal__attach">
              {uploading ? 'Uploading…' : 'Attach'}
              <input
                type="file"
                multiple
                hidden
                disabled={busy || uploading}
                onChange={(e) => void handleFiles(e.target.files)}
              />
            </label>
            {files.length > 0 ? (
              <ul className="ticket-compose-modal__file-list">
                {files.map((f, index) => (
                  <li key={`${f.path}-${index}`} className="ticket-compose-modal__file-chip">
                    <span className="ticket-compose-modal__file-name" title={f.file_name}>
                      {f.file_name}
                    </span>
                    <button
                      type="button"
                      className="ticket-compose-modal__file-remove"
                      disabled={busy || uploading}
                      aria-label={`Remove ${f.file_name}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setFiles((prev) => prev.filter((_, i) => i !== index));
                      }}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="ticket-compose-modal__actions-end">
              <button type="button" className="btn-secondary btn-sm" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary btn-sm"
                disabled={busy || uploading}
                onClick={() => void handleSend()}
              >
                {busy ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>

          {error && <p className="form-error">{error}</p>}
        </div>
      </div>
    </Modal>
  );
}
